/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { BranchesService } from '../branches.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Branch, BranchDTO } from 'src/app/_model/branches';
import { BranchesDaoService } from '../../dao/branches-dao.service';
import { IMSId, LabelFormatSetting } from 'src/app/_model/ims-object';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Branches
 */
@Injectable()
export class BranchesLogicService implements BranchesService {
  public branches$ = new BehaviorSubject<Branch[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private branchesDaoService: BranchesDaoService,
    private authenticationService: AuthenticationService,
    private appParameter: AppParameterService,
  ) {
    this.initialize();
  }

  // Public Functions
  public getBranchById(id: IMSId): Branch {
    if (id === undefined) {
      return undefined;
    }

    return this.branches$.getValue().find((sj) => {
      if (sj.id.imsId === id.imsId && sj.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  public async updateBranches(branchesDto: BranchDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new branches from other IMS component',
      branchesDto?.length,
    );

    try {
      let branches: Branch[] = [];

      for (const branchDto of branchesDto) {
        branches.push(
          Branch.fromDto(
            branchDto,
            undefined,
            this.appParameter.getFormatLabelForObject('ims/Branch'),
          ),
        );
      }

      await this.branchesDaoService.saveBranches(branches);
      await this.loadLocalBranches();
      await this.reconcileLocalBranches();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing Branches: ',
        error.message,
      );
      throw error;
    }
  }

  public async updateBranch(branch: Branch) {
    let branches = this.branches$.getValue();
    for (let b of branches) {
      if (
        b.id.imsId === branch.id.imsId ||
        b.id.secondaryId === branch.id.secondaryId
      ) {
        b = branch;
      }
    }
    this.branches$.next(branches);
    await this.branchesDaoService.saveBranches(branches);
  }

  public async createBranch(branch: Branch): Promise<Branch> {
    const created = await this.branchesDaoService.createBranch(branch);
    if (created) {
      this.branches$.next([...this.branches$.getValue(), created]);
    }
    return created;
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) branches.
    await this.branchesDaoService.reset();
    const remaining = await this.branchesDaoService.getBranches();
    this.branches$.next(remaining);
  }

  public async clear() {
    await this.branchesDaoService.clear();
    const remaining = await this.branchesDaoService.getBranches();
    this.branches$.next(remaining);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteBranches(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {
    await this.reconcileLocalBranches();
  }

  /**
   * Locally created branches have no backend id yet, so they can't be matched
   * to a synced branch by id. Once the backend creates the real branch (e.g.
   * via a form submission's postprocessor), it shows up as a separate synced
   * entry with the same label - match on that and drop the local duplicate.
   */
  private async reconcileLocalBranches(): Promise<void> {
    const all = this.branches$.getValue();
    const localBranches = all.filter((branch) => branch.isLocal());
    if (localBranches.length === 0) {
      return;
    }
    const remoteBranches = all.filter((branch) => !branch.isLocal());

    let didDelete = false;
    for (const local of localBranches) {
      const match = remoteBranches.find(
        (remote) => remote.label?.trim().toLowerCase() === local.label?.trim().toLowerCase(),
      );
      if (match) {
        this.logger.log(
          this.constructor.name,
          'Found a remote branch for local branch, removing local duplicate',
          local.label,
        );
        await this.branchesDaoService.deleteLocalBranch(local.id);
        didDelete = true;
      }
    }

    if (didDelete) {
      const remaining = await this.branchesDaoService.getBranches();
      this.branches$.next(remaining);
    }
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalBranches();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalBranches() {
    try {
      this.logger.log(this.constructor.name, '- Load local Branches');
      let branches: Branch[] = await this.branchesDaoService.getBranches();

      this.branches$.next(branches);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Branches: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteBranches(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new Branches from IMS');

      try {
        let branches: Branch[] = await this.loadRemoteBranchBatch(syncKey, []);

        this.logger.log(
          this.constructor.name,
          'Finished loading all branches (' + branches.length + ' Total)',
        );
        
        await this.loadLocalBranches()
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Branches: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  private async loadRemoteBranchBatch(
    syncKey: string,
    branches: Branch[],
    nextPart?: string
  ): Promise<Branch[]> {
    let branchesData;
    if (nextPart) {
      branchesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey
          },
        }),
      );
    } else {
      branchesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncbranches',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey
          }
        }),
      );
    }

    let nextId: string;
    let nextBranches: Branch[] = [];
    for (const branchData of branchesData) {
      if (branchData['#nextid']) {
        nextId = branchData['#nextid'];
      } else {
        nextBranches.push(
          Branch.fromDto(
            branchData,
            undefined,
            this.appParameter.getFormatLabelForObject('ims/Branch'),
          ),
        );
      }
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch Branches: ',
      nextBranches.length,
      'Total',
    );

    await this.branchesDaoService.saveBranches(nextBranches);

    let returnBranches = branches.concat(nextBranches);
    if (nextId) {
      this.logger.log(
        this.constructor.name,
        'Continue to download more branches with nextid',
        nextId,
      );
      return await this.loadRemoteBranchBatch(syncKey, returnBranches, nextId);
    }

    return returnBranches;
  }

  async initializeCompleted(): Promise<void> {}
}
